import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";
import { PageHero } from "@/components/PageHero";
import SectionOrnament from "@/components/SectionOrnament";
import Reveal from "@/components/Reveal";
import { Mail, Phone, Instagram, MapPin } from "lucide-react";

export default function ContactPage() {
  const { t } = useLanguage();
  const { content } = useContent();
  const contact = content.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  useSeo("Kontakt", "Contact", "Kontaktiert Our Promise Events per Telefon, E-Mail oder Formular.", "Contactez Our Promise Events par téléphone, e-mail ou formulaire.");

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact-messages", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero overline={t("contact.overline")} title={t("contact.title")} subtitle={t("contact.sub")} />

      <section className="relative bg-cream py-24 px-6 lg:px-10 overflow-hidden">
        <SectionOrnament variant="corners" />
        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-16">
          <Reveal>
            <div className="space-y-8">
              <a href={`mailto:${contact.email || "info@ourpromiseevents.com"}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
                <span className="w-12 h-12 border border-gold rounded-full flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-cream transition-all"><Mail size={18} strokeWidth={1.3} /></span>
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-ink-muted">{t("contact.emailLabel")}</p>
                  <p className="text-lg">{contact.email || "info@ourpromiseevents.com"}</p>
                </div>
              </a>
              <a href={`tel:${(contact.phone || "+41794848597").replace(/\s/g, "")}`} data-testid="contact-phone-link" className="flex items-center gap-4 group">
                <span className="w-12 h-12 border border-gold rounded-full flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-cream transition-all"><Phone size={18} strokeWidth={1.3} /></span>
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-ink-muted">{t("contact.phoneLabel")}</p>
                  <p className="text-lg">{contact.phone || "+41 79 484 85 97"}</p>
                </div>
              </a>
              <a href={contact.instagram || "https://instagram.com"} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="flex items-center gap-4 group">
                <span className="w-12 h-12 border border-gold rounded-full flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-cream transition-all"><Instagram size={18} strokeWidth={1.3} /></span>
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-ink-muted">{t("contact.social")}</p>
                  <p className="text-lg">{contact.instagram_handle || "Instagram"}</p>
                </div>
              </a>
              {contact.address ? (
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 border border-gold rounded-full flex items-center justify-center text-gold"><MapPin size={18} strokeWidth={1.3} /></span>
                  <p className="text-lg">{contact.address}</p>
                </div>
              ) : (
                <p className="text-sm text-ink-muted italic pl-16">{t("contact.addressPlaceholder")}</p>
              )}
            </div>
          </Reveal>

          <Reveal delay={120}>
            <form onSubmit={submit} data-testid="contact-form" className="bg-card border border-border p-8 sm:p-10 space-y-6">
              <Field testId="contact-name" label={t("contact.name")} value={form.name} onChange={handle("name")} required />
              <Field testId="contact-email" label={t("contact.email")} type="email" value={form.email} onChange={handle("email")} required />
              <Field testId="contact-phone" label={t("contact.phone")} value={form.phone} onChange={handle("phone")} />
              <div>
                <label className="text-xs tracking-[0.25em] uppercase text-ink-muted mb-2 block">{t("contact.message")}</label>
                <textarea data-testid="contact-message" value={form.message} onChange={handle("message")} required rows={5} className="w-full border-b border-border bg-transparent py-3 focus:outline-none focus:border-ink transition-colors resize-none" />
              </div>
              <button type="submit" data-testid="contact-submit" disabled={sending} className="bg-ink text-cream px-9 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-gold transition-colors duration-500 disabled:opacity-60">
                {sending ? t("common.sending") : t("contact.submit")}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ testId, label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="text-xs tracking-[0.25em] uppercase text-ink-muted mb-2 block">{label}</label>
      <input data-testid={testId} type={type} value={value} onChange={onChange} required={required} className="w-full border-b border-border bg-transparent py-3 focus:outline-none focus:border-ink transition-colors" />
    </div>
  );
}
